import { useContext, useMemo } from 'react';

import type { AllowedFeatures } from '@atlassian/embedded-confluence/features';
import type {
	EditPageIframePassThroughProps,
	ViewPageIframePassThroughProps,
	WhiteboardIframePassThroughProps,
} from '@atlassian/embedded-confluence/IframeElementType';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';

import { getSingleParam, RoutesContext } from '@confluence/route-manager';

const QUERY_NAME = 'initialAllowedFeatures';

export enum FeatureMode {
	VIEW = 'view',
	EDIT = 'edit',
	WHITEBOARD = 'whiteboard',
}

export type UseAllowedFeaturesType<T> = AllowedFeatures<T> | undefined;

export type FeatureModeType<T> = T extends FeatureMode.VIEW
	? ViewPageIframePassThroughProps
	: T extends FeatureMode.EDIT
		? EditPageIframePassThroughProps
		: WhiteboardIframePassThroughProps;

export function useAllowedFeatures<T extends FeatureMode>(): UseAllowedFeaturesType<T> {
	const { allowedFeatures } = useReactProps_DO_NOT_USE<FeatureModeType<T>>() || {};

	const { getQueryParams } = useContext(RoutesContext);

	/**
	 * When allowedFeatures passthrough props not available but query param `initialAllowedFeatures` available, we resolve allowedFeatures from this query param
	 * After allowedFeatures passthrough props become available again at SPA, the allowedFeatures should be resolved by passthrough props
	 * This is needed for rendering consistent features between SSR and SPA phases, as SSR phase cannot resolve passthrough props through `window.postMessage`
	 *
	 * E.g.
	 * - `?embedded=true&initialAllowedFeatures=edit,delete` should only enable Edit and Delete features in SSR phase
	 * - `?embedded=true&initialAllowedFeatures=all` should enable all features in SSR phase
	 * - `?embedded=true&initialAllowedFeatures=` should enable no features in SSR phase
	 */
	const queriedAllowedFeatures = useMemo(
		() => getSingleParam(getQueryParams(), QUERY_NAME),
		[getQueryParams],
	);

	const initialAllowedFeatures = useMemo(() => {
		let features;

		if (queriedAllowedFeatures === '') {
			features = [];
		} else if (queriedAllowedFeatures === 'all') {
			features = 'all';
		} else {
			// There is a bug to be fixed where comma "," in query param will break view page SSR https://product-fabric.atlassian.net/browse/SCALE-5274
			// Workaround is to allow "." as query param separator instead of comma ",". Use regex here to support both characters.
			features =
				queriedAllowedFeatures?.split(/[\s,.]+/).map((feature) => {
					try {
						return JSON.parse(feature);
					} catch (error) {
						return feature;
					}
				}) ?? undefined;
		}
		return features;
	}, [queriedAllowedFeatures]);

	return !allowedFeatures && initialAllowedFeatures ? initialAllowedFeatures : allowedFeatures;
}
